import { BitkitButton, BitkitExpandableCard, BitkitTextInput, IconMail } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { getNotifiedEmails } from '@/hooks/api/useArtifacts';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { Artifact } from '@/models/Artifact';
import { emailValidationRule, userGroupValidationRule } from '@/utils/formValidators/rules';

import { useArtifactTracking } from '../../ArtifactTrackingProvider/ArtifactTrackingProvider';

interface ArtifactNotificationSettingsProps {
  artifactId: number;
  isPublicInstallPageEnabled: boolean;
  notifiedEmailsProp: string[];
}

type MutateResponse = {
  data: Artifact;
};

const ArtifactNotificationSettings = ({
  artifactId,
  isPublicInstallPageEnabled,
  notifiedEmailsProp,
}: ArtifactNotificationSettingsProps) => {
  const [notifiedEmails, setNotifiedEmails] = useState(notifiedEmailsProp);
  const [isExpanded, setIsExpanded] = useState(false);
  const segmentTrack = useArtifactTracking();

  const {
    formState: { errors: errorsUserGroups },
    handleSubmit: handleSubmitUserGroups,
    register: registerUserGroups,
    resetField: resetFieldUserGroups,
  } = useForm<{ userGroupsToNotify: string }>();
  const userGroupsToNotifyProps = registerUserGroups('userGroupsToNotify', userGroupValidationRule);

  // emailsToNotify form
  const {
    formState: { errors: errorsEmails },
    handleSubmit: handleSubmitEmails,
    register: registerEmails,
    resetField: resetFieldEmails,
  } = useForm<{ emailsToNotify: string }>();
  const emailsToNotifyProps = registerEmails('emailsToNotify', {
    disabled: !isPublicInstallPageEnabled,
    ...emailValidationRule,
  });

  const { mutate: notifyUsers, isPending: isLoading } = useMonolithApiMutation(
    `/artifact/${artifactId}/notify.json`,
    'post',
    {
      onSuccess: ({ data }: MutateResponse) => {
        setNotifiedEmails(getNotifiedEmails(data.notifiedEmails, data.notifiedUsers));
      },
    },
  );

  const onSubmitUserGroups = handleSubmitUserGroups((value) => {
    notifyUsers({ notify_emails: '', notify_user_groups: value.userGroupsToNotify });
    resetFieldUserGroups('userGroupsToNotify');
    segmentTrack('Artifact Notification Sent', {
      event_scope: 'user',
      notification_recipient_selection_method: 'role',
      notified_roles: value.userGroupsToNotify,
    });
  });
  const onSubmitEmails = handleSubmitEmails((value) => {
    notifyUsers({ notify_emails: value.emailsToNotify, notify_user_groups: '' });
    resetFieldEmails('emailsToNotify');
    segmentTrack('Artifact Notification Sent', {
      event_scope: 'user',
      notification_recipient_selection_method: 'email',
      notified_email_count: notifiedEmails.length,
    });
  });

  const title = (
    <Text as="span">
      Notifications{' '}
      <Text color="text/tertiary" as="span">
        · {notifiedEmails.length} people notified
      </Text>
    </Text>
  );

  const onChange = (isOpened: boolean) => {
    setIsExpanded(isOpened);
    if (isOpened) {
      segmentTrack('Artifact Notification Expanded', {
        event_scope: 'user',
      });
    }
  };

  return (
    <BitkitExpandableCard elevation={false} expanded={isExpanded} onChange={onChange} title={title}>
      <Box
        display="grid"
        gap="32"
        gridTemplateColumns={['repeat(1, 1fr)', 'repeat(2, 1fr)']}
        gridAutoRows="max-content"
      >
        <chakra.form name="userGroupsToNotify" onSubmit={onSubmitUserGroups}>
          <BitkitTextInput
            label="Notify people by role"
            id={`userGroupsToNotify_${artifactId}`}
            state={errorsUserGroups.userGroupsToNotify ? 'error' : undefined}
            helperText="Enter a role (Testers, Developers, Platform engineers, Admins, Owners or Everyone)"
            errorText={errorsUserGroups.userGroupsToNotify?.message}
            endAddon={
              <BitkitButton variant="secondary" type="submit" state={isLoading ? 'loading' : undefined} icon={IconMail}>
                Notify
              </BitkitButton>
            }
            optional
            inputProps={userGroupsToNotifyProps}
          />
        </chakra.form>
        <chakra.form name="emailsToNotify" onSubmit={onSubmitEmails}>
          <BitkitTextInput
            label="Notify people by email"
            id={`emailsToNotify_${artifactId}`}
            state={!isPublicInstallPageEnabled ? 'disabled' : errorsEmails.emailsToNotify ? 'error' : undefined}
            helperText="Enter an email address"
            errorText={errorsEmails.emailsToNotify?.message}
            endAddon={
              <BitkitButton
                variant="secondary"
                type="submit"
                state={isLoading ? 'loading' : !isPublicInstallPageEnabled ? 'disabled' : undefined}
                icon={IconMail}
              >
                Notify
              </BitkitButton>
            }
            optional
            inputProps={emailsToNotifyProps}
          />
        </chakra.form>
        <Box>
          <Text textStyle="sans/size-2/bold" color="text/tertiary" textTransform="uppercase">
            People notified
          </Text>
          <Separator size="2" />
          <Box display="flex" flexDirection="column" gap="16">
            {(notifiedEmails.length > 0 ? notifiedEmails : ['No people notified']).map((notifiedListItem) => (
              <Text key={notifiedListItem} color="text/primary">
                {notifiedListItem}
              </Text>
            ))}
          </Box>
        </Box>
      </Box>
    </BitkitExpandableCard>
  );
};

export default ArtifactNotificationSettings;
